import React, { useState, useEffect } from 'react';
import { Globe, Link as LinkIcon, Check, X, ExternalLink, RefreshCw, Loader2, CheckCircle, AlertCircle, ChevronDown, Trash2 } from 'lucide-react';

const SITES_HISTORY_KEY = 'cbn_google_sites_history';

const getSavedUrls = () => {
  try {
    return JSON.parse(localStorage.getItem(SITES_HISTORY_KEY) || '[]');
  } catch { return []; }
};

const addSavedUrl = (url) => {
  if (!url) return;
  const existing = getSavedUrls().filter(u => u !== url);
  const updated = [url, ...existing].slice(0, 10);
  localStorage.setItem(SITES_HISTORY_KEY, JSON.stringify(updated));
};

const removeSavedUrl = (url) => {
  const updated = getSavedUrls().filter(u => u !== url);
  localStorage.setItem(SITES_HISTORY_KEY, JSON.stringify(updated));
};

const GoogleSitesIntegration = ({ googleSiteUrl = '', onUrlUpdate, onPortfolioItemsUpdate, saveStatus }) => {
  const [url, setUrl] = useState(googleSiteUrl);
  const [isEditing, setIsEditing] = useState(!googleSiteUrl);
  const [isFetching, setIsFetching] = useState(false);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [savedUrls, setSavedUrls] = useState(getSavedUrls);

  useEffect(() => {
    if (googleSiteUrl) {
      setUrl(googleSiteUrl);
      setIsEditing(false);
      addSavedUrl(googleSiteUrl);
      setSavedUrls(getSavedUrls());
    }
  }, [googleSiteUrl]);

  const handleSave = () => {
    if (url && isValidGoogleSiteUrl(url)) {
      addSavedUrl(url);
      setSavedUrls(getSavedUrls());
      if (onUrlUpdate) {
        onUrlUpdate(url);
      }
      setIsEditing(false);
      setError(null);
      setShowHistory(false);
      fetchPortfolioItems(url);
    } else {
      setError('Please enter a valid Google Sites URL');
    }
  };

  const handleRemove = () => {
    setUrl('');
    if (onUrlUpdate) {
      onUrlUpdate('');
    }
    setIsEditing(true);
    setPortfolioItems([]);
  };

  const handleSelectFromHistory = (savedUrl) => {
    setUrl(savedUrl);
    setShowHistory(false);
    setError(null);
  };

  const handleRemoveFromHistory = (e, savedUrl) => {
    e.stopPropagation();
    removeSavedUrl(savedUrl);
    setSavedUrls(getSavedUrls());
  };

  const isValidGoogleSiteUrl = (urlString) => {
    try {
      const urlObj = new URL(urlString);
      return urlObj.hostname.includes('sites.google.com') || urlObj.hostname.includes('google.com');
    } catch {
      return false;
    }
  };

  const fetchPortfolioItems = async (siteUrl) => {
    setIsFetching(true);
    setError(null);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const simulatedItems = [
        {
          id: '1',
          title: 'Portfolio Item from Google Sites',
          description: 'This item was imported from your Google Sites page. Click to view the full content.',
          url: siteUrl,
          type: 'link',
          thumbnail: null
        }
      ];
      
      setPortfolioItems(simulatedItems);
      if (onPortfolioItemsUpdate) {
        onPortfolioItemsUpdate(simulatedItems);
      }
    } catch (err) {
      console.error('Error fetching portfolio items:', err);
      setError('Unable to fetch portfolio items. Please ensure the site is publicly accessible.');
    } finally {
      setIsFetching(false);
    }
  };

  const convertGoogleSiteUrl = (inputUrl) => {
    try {
      const urlObj = new URL(inputUrl);
      if (urlObj.pathname.includes('/edit')) {
        const pathParts = urlObj.pathname.split('/');
        const siteIndex = pathParts.findIndex(p => p === 'd');
        if (siteIndex !== -1 && pathParts[siteIndex + 1]) {
          const siteId = pathParts[siteIndex + 1];
          return `https://sites.google.com/view/${siteId}`;
        }
      }
      return inputUrl;
    } catch {
      return inputUrl;
    }
  };

  const viewUrl = url ? convertGoogleSiteUrl(url) : '';

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Globe className="w-5 h-5 text-primary-600" />
          Google Sites Integration
        </h3>
        <div className="flex items-center gap-2">
          {/* Save status indicator */}
          {saveStatus === 'saving' && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving...
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="inline-flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="w-3.5 h-3.5" /> Saved
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="inline-flex items-center gap-1 text-xs text-red-600">
              <AlertCircle className="w-3.5 h-3.5" /> Save failed
            </span>
          )}

          {!isEditing && url && (
            <>
              <button
                onClick={() => fetchPortfolioItems(url)}
                disabled={isFetching}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium inline-flex items-center gap-1"
              >
                {isFetching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Fetching...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                  </>
                )}
              </button>
              <button
                onClick={() => setIsEditing(true)}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                Edit
              </button>
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Google Sites URL
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setError(null);
                }}
                placeholder="https://sites.google.com/... or paste your edit URL"
                className="input flex-1"
              />
              <button
                onClick={handleSave}
                className="btn btn-primary inline-flex items-center space-x-2"
              >
                <Check className="w-4 h-4" />
                <span>Save</span>
              </button>
              {googleSiteUrl && (
                <button
                  onClick={() => {
                    setUrl(googleSiteUrl);
                    setIsEditing(false);
                    setError(null);
                  }}
                  className="btn btn-secondary inline-flex items-center"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            {error && (
              <p className="text-sm text-red-600 mt-1">{error}</p>
            )}

            {/* Saved URLs dropdown */}
            {savedUrls.length > 0 && (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowHistory(!showHistory)}
                  className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
                  Previously used ({savedUrls.length})
                </button>
                {showHistory && (
                  <div className="mt-1 border border-gray-200 rounded-lg bg-white shadow-sm max-h-40 overflow-y-auto">
                    {savedUrls.map((savedUrl) => (
                      <div
                        key={savedUrl}
                        onClick={() => handleSelectFromHistory(savedUrl)}
                        className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                      >
                        <span className="text-gray-700 truncate flex-1 mr-2">{savedUrl}</span>
                        <button
                          type="button"
                          onClick={(e) => handleRemoveFromHistory(e, savedUrl)}
                          className="text-gray-400 hover:text-red-500 flex-shrink-0"
                          title="Remove from history"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-2">
              Connect your Google Sites page to import and display additional portfolio items. 
              You can paste either the view URL or edit URL - we'll automatically convert it.
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Example: <code className="bg-gray-100 px-1 rounded">https://sites.google.com/view/yoursite</code>
            </p>
          </div>
        </div>
      ) : (
        url && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
              <LinkIcon className="w-5 h-5 text-primary-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Connected</p>
                <a
                  href={viewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:underline break-all"
                >
                  {viewUrl}
                </a>
              </div>
              <a
                href={viewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline text-sm inline-flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Visit Site
              </a>
            </div>

            {/* Portfolio Items from Google Sites */}
            {portfolioItems.length > 0 && (
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">
                  Portfolio Items from Google Sites ({portfolioItems.length})
                </h4>
                <div className="space-y-2">
                  {portfolioItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                    >
                      <Globe className="w-4 h-4 text-gray-400" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.title}</p>
                        {item.description && (
                          <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                        )}
                      </div>
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Note: To import more items, manually add them to your portfolio or ensure your Google Sites page is publicly accessible.
                </p>
              </div>
            )}

            {!isFetching && portfolioItems.length === 0 && (
              <div className="text-center py-4 text-sm text-gray-500">
                <p>No portfolio items found. Click "Refresh" to fetch items from your Google Sites page.</p>
                <p className="text-xs mt-1">Note: This feature requires your site to be publicly accessible.</p>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default GoogleSitesIntegration;
